import * as React from "react";
import { cn } from "@/lib/utils";
import { iconPlus, iconMinus } from "@/lib/assets";
import { Accordion, type AccordionItemConfig } from "@/shared/ui/accordion/Accordion";
import { Card } from "@/shared/ui/card";
import { InternalLink } from "@/shared/ui/internal-link";
import { faqItems, type FaqAnswerSegment } from "@/lib/seo/faqContent";

type FaqProps = React.HTMLAttributes<HTMLElement>;

/**
 * Render an answer from `faqContent`, mapping its link segments to tracked internal links.
 * Shared by audience pages and the preserved narrow homepage.
 */
const renderAnswer = (answer: readonly FaqAnswerSegment[]) =>
  answer.map((segment, index) =>
    typeof segment === "string" ? (
      <React.Fragment key={index}>{segment}</React.Fragment>
    ) : (
      <InternalLink
        key={index}
        analyticsElementId={segment.analyticsElementId}
        href={segment.href}
        className="text-(--color-text-primary) underline"
      >
        {segment.text}
      </InternalLink>
    ),
  );

export function Faq({ className, ...props }: FaqProps) {
  const accordionItems = React.useMemo<AccordionItemConfig[]>(
    () =>
      faqItems.map((item) => ({
        id: item.question,
        title: item.question,
        titleProps: { className: "text-heading-sm", itemProp: "name" },
        content: renderAnswer(item.answer),
        defaultOpen: item.defaultOpen,
        collapsedIcon: (
          <img src={iconPlus} alt="Expand" className="block h-6 w-6 object-contain" loading="lazy" decoding="async" />
        ),
        expandedIcon: (
          <img
            src={iconMinus}
            alt="Collapse"
            className="block h-6 w-6 object-contain"
            loading="lazy"
            decoding="async"
          />
        ),
        analyticsElementId: "landing-faq:toggle",
        itemProps: { className: "w-full rounded-3xl", itemScope: true, itemType: "https://schema.org/Question" },
        contentWrapperProps: {
          itemScope: true,
          itemProp: "acceptedAnswer",
          itemType: "https://schema.org/Answer",
        },
        contentInnerProps: {
          itemProp: "text",
        },
      })),
    [],
  );

  return (
    <Card
      asChild
      variant="marketing-veil"
      padding="marketing"
      className={cn("w-full md:flex-row md:gap-24", className)}
    >
      <section aria-labelledby="faq-heading" {...props}>
        {/* Sticky from md up: the heading is a few lines beside an accordion many times its
            height, so at rest it left the column below it blank for most of the section. */}
        <div className="flex flex-1 flex-col gap-3 md:sticky md:top-6 md:self-start">
          {/* Secondary, not tertiary: tertiary measures ~2.4:1 in light mode, and only
              secondary clears 4.5:1 on every surface (see AGENTS.md's contrast table). */}
          <p className="text-heading-sm text-(--color-text-secondary)">Frequently Asked Questions</p>
          <h2 id="faq-heading" className="text-heading-lg text-(--color-text-primary)">
            Straight answers for enterprise buyers
          </h2>
        </div>

        <Accordion
          items={accordionItems}
          className="flex flex-1 flex-col items-start justify-center gap-4 md:gap-2"
          showDividers
          dividerClassName="bg-(--color-text-primary)"
        />
      </section>
    </Card>
  );
}
